import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import ReactSelect from 'react-select';
import { Collapse, Label, Spinner as ReactSpinner } from 'reactstrap';
import dayjs, { Dayjs } from 'dayjs';

import { WeeklyPlannerDatasetOptions, WeeklyPlannerDateRangeOptions } from '~constants/maps';

import { dateInputFieldFormat } from '~libs/dayjs';
import { LoadingState } from '~libs/reduxUtils';
import { useReduxSearch } from '~libs/searchParams';

import { useFocusAppointment } from '~weekly-planner/hooks/useFocusAppointment';

import ClickableIcon from '~components/ClickableIcon';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass } from '~components/icon';
import Modal from '~components/Modal';
import WeekPicker from '~components/WeekPicker';
import { AppointmentExceptions, HoldPeriods, UserExceptions } from '~weekly-planner/components/modal';

import { clear as clearAppointments } from '~weekly-planner/reducers/appointments/appointments';
import { clear as clearConflicts } from '~weekly-planner/reducers/appointments/conflicts';
import { clear as clearMissingQualifications } from '~weekly-planner/reducers/appointments/missingQualifications';
import { clear as clearRejected } from '~weekly-planner/reducers/appointments/rejected';
import { clear as clearUnallocated } from '~weekly-planner/reducers/appointments/unallocated';
import { clear as clearAwardAlerts } from '~weekly-planner/reducers/awardAlerts';
import { clear as clearUsers } from '~weekly-planner/reducers/users/users';
import {
  focusWorker,
  patchParams,
  setSearchParams as setSearchParamsAction,
  toggleOption,
  updatePublishDate,
} from '~weekly-planner/reducers/weeklyPlanner';

import { selectCompanySettings } from '~main/selectors/login';
import { selectParams, selectPublishDate, selectSearchParams } from '~weekly-planner/selectors';
import { selectExceptions, selectOnHold } from '~weekly-planner/selectors/appointments';
import { selectAllLoading } from '~weekly-planner/selectors/simulate';
import { selectRunningLate } from '~weekly-planner/selectors/users/runningLate';

import ChangesPill from './ChangesPill';
import InfoDropdown from './InfoDropdown';
import LegendDropdown from './LegendDropdown';
import PublishPill from './PublishPill';

interface ComponentProps {
  onRefresh?: (searchParams?: { repeat: boolean; sort: string[]; limit: number }, force?: boolean) => void;
  searchFields: any;
  loadingState: LoadingState;
}

const Banner: React.FC<ComponentProps> = ({ onRefresh, searchFields, loadingState }) => {
  const viewOptions = Object.values(WeeklyPlannerDatasetOptions).map((option) => ({
    value: option,
    label: option,
  }));
  const dateRangeOptions = Object.values(WeeklyPlannerDateRangeOptions).map((option) => ({
    value: option,
    label: option,
  }));

  const { rescheduled, cancelled } = useAppSelector(selectExceptions);
  const onHold = useAppSelector(selectOnHold);
  const { appointmentsBeforeNow, appointmentsNow, notLoggedIn } = useAppSelector(selectRunningLate);
  const settings = useAppSelector(selectCompanySettings);
  const publishDate = useAppSelector(selectPublishDate);
  const isAllLoading = useAppSelector(selectAllLoading);
  const params = useAppSelector(selectParams);
  const dispatch = useAppDispatch();
  const { unfocus: unfocusAppointment } = useFocusAppointment();

  const { date, viewType, rangeType } = params;
  const exceptionCount = rescheduled.length + cancelled.length;
  const onHoldCount = onHold.length;
  const runningLateCount = appointmentsBeforeNow.length + appointmentsNow.length + notLoggedIn.length;

  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [isSearchBarOpen, setIsSearchBarOpen] = useState<boolean>(false);
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);

  useEffect(() => {
    if (!publishDate) {
      const currentPublishDate = settings.published_date as string;
      dispatch(updatePublishDate(currentPublishDate));
    }
  }, [settings]);

  const [modalProperties, setModalProperties] = useState<{
    header: string;
    body: 'late' | 'hold' | 'exception' | null;
  }>({
    header: '',
    body: null,
  });

  // Use Redux for all search params
  const [searchParams, setSearchParams] = useReduxSearch(
    'search',
    selectSearchParams,
    setSearchParamsAction,
    {},
    'object',
  );

  const search = (values: { [key: string]: any }) => {
    setSearchParams(values);
  };

  const isLoading = isAllLoading || loadingState === 'pending';

  const onDateChange = (value: Dayjs) => {
    resetUnchangedData();
    resetSidebarData();
    dispatch(patchParams({ date: value.format(dateInputFieldFormat) }));
  };

  const changeView = (value: any) => {
    resetUnchangedData();
    dispatch(patchParams({ viewType: value }));
    dispatch(toggleOption({ key: 'workerAvailability', toggle: false }));
  };

  const changeRange = (value: any) => {
    resetUnchangedData();
    dispatch(patchParams({ rangeType: value }));
  };

  const resetUnchangedData = () => {
    if (!searchParams) dispatch(clearUsers([]));
    unfocusAppointment();
    dispatch(focusWorker(null));
  };

  const resetSidebarData = () => {
    dispatch(clearAppointments([]));
    dispatch(clearConflicts([]));
    dispatch(clearMissingQualifications([]));
    dispatch(clearRejected([]));
    dispatch(clearUnallocated([]));
    dispatch(clearAwardAlerts([]));
  };

  useEffect(() => {
    onRefresh?.(searchParams);
  }, [searchParams]);

  return (
    <>
      <div className="nav d-flex justify-content-between bg-light p-2 mt-2 mb-2 banner">
        <div className="d-flex">
          <PublishPill date={date} publishDate={publishDate} isLoading={!date || !publishDate} onRefresh={onRefresh} />
          <ChangesPill onRefresh={onRefresh} date={date} />
          <div className="d-flex align-items-center ms-1 me-2" data-cy={'weekly-planner-global-loader'}>
            {isLoading && <ReactSpinner />}
          </div>
        </div>
        <div className="d-flex">
          <div className="d-flex align-items-center ms-1">
            <Label className="fw-bold m-2">View:</Label>
            <ReactSelect
              id="dataset-view-select"
              name="dataset-view-select"
              data-cy="weekly-planner-dataset-view-select"
              options={viewOptions}
              value={viewOptions.find((option) => option.value === viewType)}
              defaultValue={viewOptions[0]}
              closeMenuOnSelect={true}
              onChange={(option) => changeView(option?.value)}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 100 }) }}
            />
            <Label className="fw-bold m-2 ps-2">Range:</Label>
            <ReactSelect
              id="dataset-range-select"
              name="dataset-range-select"
              data-cy="weekly-planner-dataset-range-select"
              options={dateRangeOptions}
              value={dateRangeOptions.find((option) => option.value === rangeType)}
              defaultValue={dateRangeOptions[0]}
              closeMenuOnSelect={true}
              onChange={(option) => changeRange(option?.value)}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 100 }) }}
            />
          </div>
          <div className="d-flex align-items-center me-1">
            <WeekPicker initialDate={dayjs(date)} onChange={onDateChange} />
            <ClickableIcon
              id="search-toggle"
              icon={faMagnifyingGlass}
              size="small"
              className="mt-1 me-1"
              onClick={() => setIsSearchBarOpen(!isSearchBarOpen)}
              toolTip={isSearchBarOpen ? 'Hide Search' : 'Search'}
            />
            <InfoDropdown
              isInfoOpen={isInfoOpen}
              setIsInfoOpen={setIsInfoOpen}
              onHoldCount={onHoldCount}
              setShowInfoModal={setShowInfoModal}
              setShowInfoModalProperties={setModalProperties}
              runningLateCount={runningLateCount}
              exceptionCount={exceptionCount}
            />
            <LegendDropdown isOpen={isLegendOpen} setIsOpen={setIsLegendOpen} />
          </div>
        </div>
      </div>
      <Modal
        isOpen={showInfoModal}
        setIsOpen={setShowInfoModal}
        header={modalProperties.header}
        scrollable
        size="xl"
        centered
      >
        {modalProperties.body === 'exception' && <AppointmentExceptions />}
        {modalProperties.body === 'late' && <UserExceptions />}
        {modalProperties.body === 'hold' && <HoldPeriods />}
      </Modal>
      <Collapse isOpen={isSearchBarOpen}>
        <SearchBar
          fields={searchFields}
          initialValues={searchParams ?? {}}
          storageKey="weekly-planner"
          onSubmit={search}
        />
      </Collapse>
    </>
  );
};

export default Banner;
